/**
 * <usp-carousel> - rotates the USP bar one item at a time when not all USPs fit.
 *
 * All USPs are server-rendered in the HTML. The first items are cloned at the end of the track so
 * the loop never visibly jumps back; clones are aria-hidden and inert. Rotation pauses on hover,
 * keyboard focus, touch (for 10 seconds), when the tab is hidden or the bar is off screen, and
 * while a USP block is selected in the theme editor. With prefers-reduced-motion the items still
 * change, but without sliding.
 */
(function () {
  if (customElements.get('usp-carousel')) return;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Same breakpoints as the --usp-per-view values in sections/usp-bar.liquid.
  const TABLET = window.matchMedia('(min-width: 750px)');
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  class UspCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.usp-bar__track');
      if (!this.track) return;
      this.items = Array.from(this.track.children);
      this.interval = Number(this.dataset.interval) || 4000;
      this.index = 0;
      this.pauses = new Set();

      this.onTransitionEnd = this.onTransitionEnd.bind(this);
      this.onVisibility = () => this.setPaused('hidden', document.hidden);
      this.track.addEventListener('transitionend', this.onTransitionEnd);
      document.addEventListener('visibilitychange', this.onVisibility);

      this.addEventListener('mouseenter', () => this.setPaused('hover', true));
      this.addEventListener('mouseleave', () => this.setPaused('hover', false));
      this.addEventListener('focusin', () => this.setPaused('focus', true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setPaused('focus', false);
      });
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        this.setPaused('touch', true);
        clearTimeout(this.touchTimer);
        this.touchTimer = setTimeout(() => this.setPaused('touch', false), 10000);
      });

      this.intersection = new IntersectionObserver(([entry]) => this.setPaused('offscreen', !entry.isIntersecting));
      this.intersection.observe(this);
      this.resize = new ResizeObserver(() => this.setup());
      this.resize.observe(this);

      if (window.Shopify?.designMode) {
        this.onBlockSelect = (event) => {
          const position = this.items.indexOf(event.target);
          if (position === -1) return;
          this.setPaused('editor', true);
          if (this.rotating) this.goTo(Math.min(position, this.items.length - this.perView));
        };
        this.onBlockDeselect = (event) => {
          if (this.items.includes(event.target)) this.setPaused('editor', false);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    disconnectedCallback() {
      this.stop();
      this.intersection?.disconnect();
      this.resize?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    /** Re-evaluate on every width change; rebuilds only when the number of visible items changes. */
    setup() {
      let perView = 1;
      if (DESKTOP.matches) perView = Number(this.dataset.perViewDesktop) || 1;
      else if (TABLET.matches) perView = Number(this.dataset.perViewTablet) || 1;
      if (perView === this.perView) return;
      this.perView = perView;

      for (const clone of this.track.querySelectorAll('[data-clone]')) clone.remove();
      this.rotating = this.items.length > perView;
      this.toggleAttribute('data-static', !this.rotating);

      if (this.rotating) {
        for (const item of this.items.slice(0, perView)) {
          const clone = item.cloneNode(true);
          clone.dataset.clone = '';
          clone.removeAttribute('data-shopify-editor-block');
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          this.track.append(clone);
        }
      }
      this.goTo(0, false);
      this.restart();
    }

    goTo(index, animate = true) {
      this.index = index;
      this.track.style.transition = animate && !REDUCED_MOTION.matches ? '' : 'none';
      this.track.style.translate = `calc(${-index} * 100% / ${this.perView}) 0`;
      if (!animate || REDUCED_MOTION.matches) {
        this.track.getBoundingClientRect(); // apply the jump before transitions come back
        this.track.style.transition = '';
        if (index >= this.items.length) this.goTo(0, false);
      }
    }

    /** After sliding onto the clones, jump back to the real first item without animation. */
    onTransitionEnd(event) {
      if (event.target === this.track && this.index >= this.items.length) this.goTo(0, false);
    }

    next() {
      this.goTo(this.index + 1);
    }

    setPaused(reason, paused) {
      if (paused) this.pauses.add(reason);
      else this.pauses.delete(reason);
      this.restart();
    }

    restart() {
      this.stop();
      if (this.rotating && this.pauses.size === 0) {
        this.timer = setInterval(() => this.next(), this.interval);
      }
    }

    stop() {
      clearInterval(this.timer);
    }
  }

  customElements.define('usp-carousel', UspCarousel);
})();
